import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { signExpiringToken, verifyExpiringToken } from "./shareToken.server";
import {
  analyzeLegacyScore,
  DIMENSIONS,
  QUESTIONS,
  type Responses,
} from "./legacyScore";

const SessionId = z.string().min(8).max(64);

// Validate that every responses key is a known `<dimension>.<questionId>`
// and value is one of the question's allowed choices.
const ResponsesSchema = z.record(z.string().min(1).max(120), z.string().min(1).max(60)).superRefine(
  (val, ctx) => {
    const allowed = new Map<string, Set<string>>();
    for (const dim of DIMENSIONS) {
      for (const q of QUESTIONS[dim]) {
        allowed.set(
          `${dim}.${q.id}`,
          new Set(q.choices.map((c) => c.value)),
        );
      }
    }
    for (const [k, v] of Object.entries(val)) {
      const set = allowed.get(k);
      if (!set) {
        ctx.addIssue({ code: "custom", path: [k], message: `Unknown question: ${k}` });
      } else if (!set.has(v)) {
        ctx.addIssue({ code: "custom", path: [k], message: `Invalid value for ${k}` });
      }
    }
  },
);

const signToken = (id: string): string => signExpiringToken(id);
const verifyToken = (token: string): string | null =>
  verifyExpiringToken(token);

export const submitLegacyScore = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        session_id: SessionId,
        email: z.string().trim().toLowerCase().email().max(255),
        marketing_opt_in: z.boolean().default(true),
        responses: ResponsesSchema,
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const analysis = analyzeLegacyScore(data.responses as Responses);

    // Look up most recent prior score for this email for delta tracking.
    let previous_score: number | null = null;
    try {
      const { data: prior } = await supabaseAdmin
        .from("legacy_scores")
        .select("score_total")
        .eq("email", data.email)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (prior && typeof prior.score_total === "number") {
        previous_score = prior.score_total;
      }
    } catch {
      // Non-fatal — proceed without delta.
    }

    const perDim = Object.fromEntries(
      analysis.per_dimension.map((d) => [d.dimension, d.score]),
    );

    const { data: inserted, error } = await supabaseAdmin
      .from("legacy_scores")
      .insert({
        session_id: data.session_id,
        email: data.email,
        responses: data.responses,
        score_total: analysis.total,
        score_protection: perDim.protection ?? 0,
        score_income: perDim.income ?? 0,
        score_retirement: perDim.retirement ?? 0,
        score_estate: perDim.estate ?? 0,
        score_beneficiaries: perDim.beneficiaries ?? 0,
        score_education: perDim.education ?? 0,
        previous_score,
        improvement_actions: analysis.improvement_actions,
        completed_at: new Date().toISOString(),
      } as never)
      .select("id")
      .single();

    if (error || !inserted) {
      return { ok: false as const, error: error?.message ?? "Failed to save score." };
    }

    // TODO: send-legacy-score-email — wire Resend + PDF attachment when available.
    console.log(
      `[send-legacy-score-email] queue legacy score ${inserted.id} to ${data.email}`,
    );

    return { ok: true as const, token: signToken(inserted.id) };
  });

export const loadLegacyScore = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(8).max(300) }).parse(input),
  )
  .handler(async ({ data }) => {
    const id = verifyToken(data.token);
    if (!id) return { ok: false as const, error: "Link expired or invalid." };
    const { data: row, error } = await supabaseAdmin
      .from("legacy_scores")
      .select(
        "id, email, responses, score_total, score_protection, score_income, score_retirement, score_estate, score_beneficiaries, score_education, previous_score, improvement_actions, completed_at, created_at",
      )
      .eq("id", id)
      .maybeSingle();
    if (error || !row) return { ok: false as const, error: "Score not found." };
    return { ok: true as const, score: row };
  });